import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../core/branding/BrandingContext';
import { useAuth } from '../components/auth/SimpleAuthProvider';

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { branding, projectName } = useBranding();
  const { user, logout } = useAuth();

  if (!branding) {
    return <div>Loading...</div>;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOpenMap = () => {
    navigate('/map');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: branding.backgroundColor }}>
      {/* Header */}
      <header
        className="shadow-md py-4 px-8 flex justify-between items-center"
        style={{ backgroundColor: branding.primaryColor }}
      >
        <div className="flex items-center space-x-4">
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={`${projectName} logo`}
              style={{ height: 120, width: 'auto', objectFit: 'contain', display: 'block' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <h1 className="text-xl font-bold text-white">
            {branding.headingText}
          </h1>
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-white text-sm">
            {user?.firstName ? `${user.firstName} ${user.lastName}`.trim() : user?.email}
          </span>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-white rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
            style={{ color: branding.primaryColor }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-8 py-12">
        <div className="mb-8">
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: branding.primaryColor }}
          >
            Welcome, {user?.firstName || user?.email.split('@')[0]}! 👋
          </h2>
          <p className="text-gray-600">
            {branding.subheadingText}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            className="col-span-full bg-white rounded-lg shadow-lg p-6 cursor-pointer hover:shadow-2xl transition-shadow flex flex-col md:flex-row items-center"
            onClick={handleOpenMap}
          >
            <div
              className="flex-shrink-0 w-20 h-20 rounded-lg flex items-center justify-center mr-6"
              style={{ background: branding.primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: '#fff' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>

            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-2" style={{ color: branding.primaryColor }}>
                Open Map
              </h3>
              <p className="text-gray-600 mb-4">
                Launch the interactive geospatial map viewer. Explore layers, toggle datasets, and analyze the map in 3D.
              </p>
              <div>
                <button
                  onClick={handleOpenMap}
                  className="px-6 py-2 rounded-lg font-semibold text-white"
                  style={{ backgroundColor: branding.primaryColor }}
                >
                  Open Map
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Project Info */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4" style={{ color: branding.primaryColor }}>
            Project Information
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Project:</span>
              <span className="ml-2 font-semibold">{projectName}</span>
            </div>
            <div>
              <span className="text-gray-600">User:</span>
              <span className="ml-2 font-semibold">{user?.email}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};